import Svg from 'components/UI/SVG'
import { graphql, useStaticQuery } from 'gatsby'
import InstantSearchSVG from 'images/instant-search.svg'
import { Dispatch, ReactElement, SetStateAction, useEffect, useRef, useState } from 'react'
import { connectHits, InstantSearch, SearchBox } from 'react-instantsearch-dom'
import { Flex } from 'theme-ui'
import useBreakpointIndex from 'theme/useBreakpointIndex'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { StringParam, useQueryParam } from 'use-query-params'
import getEnglishStrings from 'utils/getEnglishStrings'
import transformProductionApiUrlTld from 'utils/transformProductionApiUrlTld'
import Hits from './Hits'
import './instant-search.css'

const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
  server: {
    apiKey: process.env.GATSBY_TYPESENSE_SEARCH_API_KEY ?? '',
    nodes: [
      {
        host: transformProductionApiUrlTld(process.env.GATSBY_TYPESENSE_API_URL ?? ''),
        port: 443,
        protocol: 'https',
      },
    ],
  },
  // The following parameters are directly passed to Typesense's search API endpoint.
  //  So you can pass any parameters supported by the search endpoint below.
  //  queryBy is required.
  additionalSearchParameters: {
    query_by: 'title, body',
  },
})
const searchClient = typesenseInstantsearchAdapter.searchClient

export default function SearchInterface({
  hasInputValue,
  setHasInputValue,
}: {
  hasInputValue: boolean
  setHasInputValue: Dispatch<SetStateAction<boolean>>
}): ReactElement {
  const data = useStaticQuery<Queries.SearchPlaceholderQuery>(graphql`
    query SearchPlaceholder {
      directus {
        knowledge_base_page {
          strings {
            languages_code {
              code
            }
            search_placeholder_mobile
            search_placeholder_desktop_tablet
          }
        }
      }
    }
  `)

  const strings = getEnglishStrings(data?.directus?.knowledge_base_page?.strings)

  const CustomHits = connectHits(Hits)
  const [searchQuery] = useQueryParam('search', StringParam)
  const inputRef = useRef<HTMLInputElement>(null)

  const breakpointIndex = useBreakpointIndex()
  const placeholderText = (() => {
    if (breakpointIndex === 0) {
      return strings?.search_placeholder_mobile ?? ''
    } else if (breakpointIndex > 0) {
      return strings?.search_placeholder_desktop_tablet ?? ''
    } else {
      return ''
    }
  })()

  const isMobileOrTablet = breakpointIndex === 0 || breakpointIndex === 1

  useEffect(() => {
    setHasInputValue(!!searchQuery && searchQuery !== '')
  }, [searchQuery, setHasInputValue])

  // unfortunately this is actually the type that React InstantSearch uses for its search state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [searchState, setSearchState] = useState<any>({ query: '' })

  useEffect(() => {
    // we need to actually trigger a change from a `useEffect` to initialize the search
    // state from the query string because in SSR'd builds it will not otherwise toggle out
    // of search mode on clearing the input
    setSearchState({ query: searchQuery })

    // we only want to do this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName="articles"
      searchState={searchState}
      onSearchStateChange={args => {
        setSearchState(args)
        setHasInputValue(!!args.query && args.query !== '')
      }}
    >
      <Flex
        sx={{ width: '100%' }}
        onClick={() => {
          isMobileOrTablet &&
            document.getElementById('instantSearch_input')?.scrollIntoView({ behavior: 'smooth' })
        }}
      >
        <SearchBox
          translations={{
            placeholder: placeholderText,
          }}
          inputId="instantSearch_input"
          inputRef={inputRef}
          submit={<Svg svg={InstantSearchSVG} sx={{ mt: '0.6rem', m: '0.4rem 0.4rem 0px 0px' }} />}
          reset={<Svg svg={InstantSearchSVG} sx={{ mt: '0.6rem', m: '0.4rem 0.4rem 0px 0px' }} />}
          onChange={event => setHasInputValue((event.target as HTMLInputElement).value !== '')}
          sx={{
            'input[type="search"]': {
              scrollMarginTop: '9.6rem',
              color: 'black',
            },
            '.ais-SearchBox-reset': {
              border: 'none',
              background: 'transparent',
            },
            '.ais-SearchBox-input': {
              variant: 'text.paragraphSmallRegular',
            },
            mb: '5.6rem',
            form: {
              maxHeight: '4.8rem',
            },
          }}
        />
      </Flex>
      {hasInputValue && <CustomHits />}
    </InstantSearch>
  )
}
